/*
 * Premium Search for Shopify (Dawn compatible)
 * ------------------------------------------------------------------
 * - In-page dropdown, no navigation on icon click
 * - Live product / collection / page / article / query suggestions
 * - Recent searches + previously searched products (localStorage)
 * - Keyboard navigation, ARIA combobox, screen-reader announcements
 * - Quick add to cart for single-variant products
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  var STORE_KEYS = {
    queries: 'premium-search:queries',
    products: 'premium-search:products',
  };

  /* ---------------------------------------------------------------- utils */

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments,
        self = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeRegex(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Wraps the searched term inside escaped text — WITHOUT ever running the
  // match regex over already-escaped HTML. The previous version escaped
  // first and matched second, which broke apart entities like "&amp;"
  // whenever the search term itself contained a character such as "&":
  // the regex would match the literal "&" sitting *inside* "&amp;" and
  // wrap just that character, splitting the entity into
  // "<mark>&</mark>amp;" — the browser then renders the orphaned "amp;"
  // as plain visible text. Matching on the raw text and escaping each
  // resulting segment (matched and unmatched) individually avoids this
  // entirely, for any search term.
  function highlight(text, term) {
    var str = String(text == null ? '' : text);
    if (!term) return escapeHtml(str);
    var words = term.split(/\s+/).filter(Boolean).map(escapeRegex);
    if (!words.length) return escapeHtml(str);

    var re = new RegExp('(' + words.join('|') + ')', 'gi');
    var result = '';
    var lastIndex = 0;
    var match;
    while ((match = re.exec(str)) !== null) {
      result += escapeHtml(str.slice(lastIndex, match.index));
      result += '<mark class="ps-mark">' + escapeHtml(match[0]) + '</mark>';
      lastIndex = match.index + match[0].length;
      if (match[0].length === 0) re.lastIndex += 1; // guard against zero-length matches looping forever
    }
    result += escapeHtml(str.slice(lastIndex));
    return result;
  }

  function storageGet(key) {
    try {
      var raw = window.localStorage.getItem(key);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private mode / quota — recents are a convenience, never required */
    }
  }

  // Shopify money formatting. suggest.json returns prices in cents.
  function formatMoney(cents, format) {
    if (cents == null || cents === '') return '';
    if (typeof cents === 'string') {
      if (/[^\d.,\s-]/.test(cents)) return cents; // already formatted by the theme
      cents = cents.replace(/[,.]/g, '');
    }
    var value = parseInt(cents, 10);
    if (isNaN(value)) return '';

    function pad(num, size) {
      var s = String(num);
      while (s.length < size) s = '0' + s;
      return s;
    }

    function number(precision, thousands, decimal) {
      var amount = (value / 100.0).toFixed(precision);
      var parts = amount.split('.');
      var head = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return precision ? head + decimal + pad(parts[1], precision) : head;
    }

    var template = format || '${{amount}}';
    var match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    var placeholder = match ? match[1] : 'amount';
    var out;

    switch (placeholder) {
      case 'amount_no_decimals':
        out = number(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        out = number(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = number(0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = number(2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        out = number(0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        out = number(2, ' ', ',');
        break;
      default:
        out = number(2, ',', '.');
    }
    return template.replace(/\{\{\s*\w+\s*\}\}/, out);
  }

  // Uses Shopify CDN's "width" query parameter rather than splicing a
  // "_360x" suffix into the filename. The old filename-suffix approach
  // required the URL to end in a recognized image extension (optionally
  // followed by a query string) — if the URL Shopify's predictive search
  // actually returned didn't match that exact shape (e.g. it already
  // carried its own query parameters in an order the regex didn't
  // anticipate, or lacked a plain file extension), the regex silently
  // failed to resize it and, in some cases, could hand back a URL the
  // <img> tag then failed to load — which is exactly what showed as a
  // blank placeholder box with the image "hidden" by the onerror handler.
  // A "width" query parameter is accepted by Shopify's CDN on any valid
  // image URL regardless of its existing shape, which is far harder to
  // break.
  function resizeImage(url, widthPx) {
    if (!url) return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    if (url.indexOf('http') !== 0) return url; // unrecognized shape — use as-is rather than risk breaking it
    try {
      var u = new URL(url);
      u.searchParams.set('width', String(widthPx));
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  // The suggest.json product resource has returned images under different
  // shapes across API versions/themes: a plain string, {url}, or {src}.
  // Try every shape rather than assuming one.
  function pickProductImage(product) {
    var candidates = [product.featured_image, product.image];
    if (product.images && product.images.length) candidates.push(product.images[0]);
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (!c) continue;
      if (typeof c === 'string') return c;
      if (c.url) return c.url;
      if (c.src) return c.src;
    }
    return '';
  }

  /* -------------------------------------------------------- the component */

  class PremiumSearch extends HTMLElement {
    constructor() {
      super();

      this.settings = {
        minChars: parseInt(this.dataset.minChars || '1', 10),
        debounce: parseInt(this.dataset.debounce || '200', 10),
        productLimit: parseInt(this.dataset.productLimit || '6', 10),
        otherLimit: parseInt(this.dataset.otherLimit || '3', 10),
        recentLimit: parseInt(this.dataset.recentLimit || '8', 10),
        productMemory: parseInt(this.dataset.productMemory || '8', 10),
        quickAdd: this.dataset.quickAdd === 'true',
        moneyFormat: this.dataset.moneyFormat || '${{amount}}',
        searchUrl: this.dataset.searchUrl || '/search',
        trending: (this.dataset.trending || '')
          .split(',')
          .map(function (s) {
            return s.trim();
          })
          .filter(Boolean),
      };

      this.cache = new Map();
      this.controller = null;
      this.isOpen = false;
      this.activeIndex = -1;
      this.lastQuery = '';
    }

    connectedCallback() {
      this.trigger = this.querySelector('[data-ps-trigger]');
      this.panel = this.querySelector('[data-ps-panel]');
      this.form = this.querySelector('[data-ps-form]');
      this.input = this.querySelector('[data-ps-input]');
      this.reset = this.querySelector('[data-ps-reset]');
      this.close = this.querySelector('[data-ps-close]');
      this.results = this.querySelector('[data-ps-results]');
      this.status = this.querySelector('[data-ps-status]');

      if (!this.input || !this.results) return;

      this.onInput = debounce(this.handleInput.bind(this), this.settings.debounce);

      if (this.trigger) {
        this.trigger.addEventListener('click', this.toggle.bind(this));
      } else {
        // Inline mode (search page): the panel is always in the flow.
        this.isOpen = true;
      }

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.results.addEventListener('click', this.handleResultsClick.bind(this));
      this.form.addEventListener('submit', this.handleSubmit.bind(this));

      if (this.reset) this.reset.addEventListener('click', this.handleReset.bind(this));
      if (this.close) this.close.addEventListener('click', this.hide.bind(this));

      document.addEventListener('click', this.handleOutsideClick.bind(this));
      document.addEventListener('keydown', this.handleEscape.bind(this));

      if (this.dataset.inline === 'true' && this.input.value) {
        this.lastQuery = this.input.value.trim();
      }
    }

    /* ------------------------------------------------------ open / close */

    toggle(event) {
      event.preventDefault();
      this.isOpen ? this.hide() : this.show();
    }

    show() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.setAttribute('open', '');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('ps-scroll-lock');
      /* Reuses the same protective class the mobile nav drawer uses to
         neutralize position:sticky bars (see gg-theme.css's
         html.gg-drawer-open .gg-sticky-bar rule) — sticky elements can get
         promoted to their own compositor layer on mobile browsers and
         render above other overlays regardless of z-index, which is what
         was letting the PLP's sticky "Filter / Sort by" bar bleed through
         on top of this search panel. */
      document.documentElement.classList.add('gg-drawer-open');

      var self = this;
      requestAnimationFrame(function () {
        self.input.focus();
        self.input.select();
      });

      this.input.value.trim().length >= this.settings.minChars
        ? this.search(this.input.value.trim())
        : this.renderIdle();

      this.dispatchEvent(new CustomEvent('premium-search:open', { bubbles: true }));
    }

    hide() {
      if (!this.isOpen || this.dataset.inline === 'true') {
        this.clearActive();
        return;
      }
      this.isOpen = false;
      this.removeAttribute('open');
      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.focus();
      }
      document.documentElement.classList.remove('ps-scroll-lock');
      document.documentElement.classList.remove('gg-drawer-open');
      this.clearActive();
      this.dispatchEvent(new CustomEvent('premium-search:close', { bubbles: true }));
    }

    handleOutsideClick(event) {
      if (!this.isOpen || this.dataset.inline === 'true') return;
      if (!this.contains(event.target)) this.hide();
    }

    handleEscape(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;
      if (this.activeIndex > -1) {
        this.clearActive();
        return;
      }
      this.hide();
    }

    handleFocus() {
      if (!this.input.value.trim()) this.renderIdle();
    }

    /* ----------------------------------------------------------- queries */

    handleInput() {
      var term = this.input.value.trim();
      if (this.reset) this.reset.classList.toggle('hidden', !term);

      if (term.length < this.settings.minChars) {
        this.lastQuery = '';
        this.renderIdle();
        return;
      }
      this.search(term);
    }

    handleReset(event) {
      event.preventDefault();
      this.input.value = '';
      this.lastQuery = '';
      if (this.reset) this.reset.classList.add('hidden');
      this.input.focus();
      this.renderIdle();
    }

    handleSubmit(event) {
      var term = this.input.value.trim();
      if (!term) {
        event.preventDefault();
        return;
      }
      this.rememberQuery(term);
      // Let the form submit to /search so the full results page is reachable.
    }

    search(term) {
      var self = this;
      this.lastQuery = term;

      if (this.cache.has(term)) {
        this.renderResults(this.cache.get(term), term);
        return;
      }

      this.renderLoading();

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      var params = new URLSearchParams();
      params.set('q', term);
      params.set('resources[type]', 'product,collection,page,article,query');
      params.set('resources[limit]', String(Math.max(this.settings.productLimit, this.settings.otherLimit)));
      params.set('resources[options][unavailable_products]', 'last');
      params.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');

      fetch(this.settings.searchUrl + '/suggest.json?' + params.toString(), {
        signal: this.controller.signal,
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Search request failed');
          return response.json();
        })
        .then(function (data) {
          var resources = (data.resources && data.resources.results) || {};
          self.cache.set(term, resources);
          if (self.lastQuery !== term) return; // a newer query already won
          self.renderResults(resources, term);
          self.rememberProducts(resources.products || []);
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          self.renderError();
        });
    }

    /* ---------------------------------------------------------- rendering */

    renderLoading() {
      var cards = '';
      for (var i = 0; i < 4; i++) {
        cards +=
          '<div class="ps-skeleton"><div class="ps-skeleton__media"></div>' +
          '<div class="ps-skeleton__lines"><span></span><span></span></div></div>';
      }
      this.results.innerHTML = '<div class="ps-section" aria-hidden="true">' + cards + '</div>';
      this.announce(window.PremiumSearchStrings ? window.PremiumSearchStrings.loading : 'Searching');
    }

    renderError() {
      this.results.innerHTML =
        '<div class="ps-empty"><p class="ps-empty__title">Search is unavailable right now</p>' +
        '<p class="ps-empty__body">Check your connection and try again, or press Enter to open the full search page.</p></div>';
    }

    renderIdle() {
      var queries = storageGet(STORE_KEYS.queries).slice(0, this.settings.recentLimit);
      var products = storageGet(STORE_KEYS.products).slice(0, this.settings.productMemory);
      var html = '';

      if (queries.length) {
        html +=
          '<div class="ps-section">' +
          '<div class="ps-section__head"><h2 class="ps-section__title">Recent searches</h2>' +
          '<button type="button" class="ps-link" data-ps-clear="queries">Clear</button></div>' +
          '<ul class="ps-chips" role="none">' +
          queries
            .map(function (q) {
              return (
                '<li class="ps-chip"><button type="button" class="ps-chip__go" data-ps-term="' +
                escapeHtml(q) +
                '">' +
                escapeHtml(q) +
                '</button>' +
                '<button type="button" class="ps-chip__remove" data-ps-forget="' +
                escapeHtml(q) +
                '" aria-label="Remove ' +
                escapeHtml(q) +
                ' from recent searches">&times;</button></li>'
              );
            })
            .join('') +
          '</ul></div>';
      }

      if (products.length) {
        html +=
          '<div class="ps-section">' +
          '<div class="ps-section__head"><h2 class="ps-section__title">From your last searches</h2>' +
          '<button type="button" class="ps-link" data-ps-clear="products">Clear</button></div>' +
          '<div class="ps-grid">' +
          products
            .map(this.productCard.bind(this), '')
            .join('') +
          '</div></div>';
      }

      if (!queries.length && !products.length) {
        if (this.settings.trending.length) {
          html +=
            '<div class="ps-section"><h2 class="ps-section__title">Popular right now</h2>' +
            '<ul class="ps-chips" role="none">' +
            this.settings.trending
              .map(function (q) {
                return (
                  '<li class="ps-chip"><button type="button" class="ps-chip__go" data-ps-term="' +
                  escapeHtml(q) +
                  '">' +
                  escapeHtml(q) +
                  '</button></li>'
                );
              })
              .join('') +
            '</ul></div>';
        } else {
          html =
            '<div class="ps-empty"><p class="ps-empty__title">Start typing to see products</p>' +
            '<p class="ps-empty__body">Search by product name, type, vendor or tag.</p></div>';
        }
      }

      this.results.innerHTML = html;
      this.clearActive();
    }

    renderResults(resources, term) {
      var products = (resources.products || []).slice(0, this.settings.productLimit);
      var queries = (resources.queries || []).slice(0, 4);
      var collections = (resources.collections || []).slice(0, this.settings.otherLimit);
      var pages = (resources.pages || []).slice(0, this.settings.otherLimit);
      var articles = (resources.articles || []).slice(0, this.settings.otherLimit);
      var total = products.length + collections.length + pages.length + articles.length;
      var self = this;
      var html = '';

      if (!total && !queries.length) {
        var recentProducts = storageGet(STORE_KEYS.products).slice(0, 4);
        html =
          '<div class="ps-empty"><p class="ps-empty__title">No matches for &ldquo;' +
          escapeHtml(term) +
          '&rdquo;</p><p class="ps-empty__body">Try a shorter word, or check the spelling.</p></div>';
        if (this.settings.trending.length) {
          html +=
            '<div class="ps-section"><h2 class="ps-section__title">Try one of these</h2><ul class="ps-chips" role="none">' +
            this.settings.trending
              .map(function (q) {
                return (
                  '<li class="ps-chip"><button type="button" class="ps-chip__go" data-ps-term="' +
                  escapeHtml(q) +
                  '">' +
                  escapeHtml(q) +
                  '</button></li>'
                );
              })
              .join('') +
            '</ul></div>';
        }
        if (recentProducts.length) {
          html +=
            '<div class="ps-section"><h2 class="ps-section__title">From your last searches</h2><div class="ps-grid">' +
            recentProducts.map(this.productCard.bind(this)).join('') +
            '</div></div>';
        }
        this.results.innerHTML = html;
        this.clearActive();
        this.announce('No results for ' + term);
        return;
      }

      if (queries.length) {
        html +=
          '<div class="ps-section"><h2 class="ps-section__title">Suggestions</h2><ul class="ps-list" role="none">' +
          queries
            .map(function (q) {
              return (
                '<li><a class="ps-list__item ps-option" role="option" href="' +
                escapeHtml(self.settings.searchUrl) +
                '?q=' +
                encodeURIComponent(q.text) +
                '&options%5Bprefix%5D=last" data-ps-remember-query="' +
                escapeHtml(q.text) +
                '"><span class="ps-list__icon" aria-hidden="true">&#8599;</span><span>' +
                highlight(q.text, term) +
                '</span></a></li>'
              );
            })
            .join('') +
          '</ul></div>';
      }

      if (products.length) {
        html +=
          '<div class="ps-section"><h2 class="ps-section__title">Products</h2><div class="ps-grid">' +
          products
            .map(function (p) {
              return self.productCard(p, term);
            })
            .join('') +
          '</div></div>';
      }

      function linkBlock(title, items) {
        if (!items.length) return '';
        return (
          '<div class="ps-section"><h2 class="ps-section__title">' +
          title +
          '</h2><ul class="ps-list" role="none">' +
          items
            .map(function (item) {
              return (
                '<li><a class="ps-list__item ps-option" role="option" href="' +
                escapeHtml(item.url) +
                '">' +
                highlight(item.title, term) +
                '</a></li>'
              );
            })
            .join('') +
          '</ul></div>'
        );
      }

      html += linkBlock('Collections', collections);
      html += linkBlock('Articles', articles);
      html += linkBlock('Pages', pages);

      html +=
        '<div class="ps-footer"><a class="ps-footer__link ps-option" role="option" href="' +
        escapeHtml(this.settings.searchUrl) +
        '?q=' +
        encodeURIComponent(term) +
        '&options%5Bprefix%5D=last">See all results for &ldquo;' +
        escapeHtml(term) +
        '&rdquo;</a></div>';

      this.results.innerHTML = html;
      this.clearActive();
      this.announce(total + (total === 1 ? ' result' : ' results') + ' for ' + term);
    }

    productCard(product, term) {
      var image = resizeImage(pickProductImage(product), 360);
      var price = formatMoney(product.price, this.settings.moneyFormat);
      var compare = formatMoney(product.compare_at_price, this.settings.moneyFormat);
      var onSale = compare && compare !== price && parseInt(String(product.compare_at_price).replace(/[,.]/g, ''), 10) > parseInt(String(product.price).replace(/[,.]/g, ''), 10);
      var soldOut = product.available === false;
      var variants = product.variants || [];
      var single = variants.length === 1 ? variants[0] : null;

      var payload = escapeHtml(
        JSON.stringify({
          title: product.title,
          url: product.url,
          image: product.featured_image ? product.featured_image.url : product.image,
          price: product.price,
          compare_at_price: product.compare_at_price,
          vendor: product.vendor,
          available: product.available,
          variants: single ? [{ id: single.id, available: single.available }] : [],
        })
      );

      return (
        '<article class="ps-card">' +
        '<a class="ps-card__link ps-option" role="option" href="' +
        escapeHtml(product.url) +
        '" data-ps-remember="' +
        payload +
        '">' +
        '<span class="ps-card__media">' +
        (image
          ? '<img src="' +
            escapeHtml(image) +
            '" alt="" loading="lazy" width="120" height="120" onerror="this.style.display=\'none\'">'
          : '<span class="ps-card__placeholder" aria-hidden="true"></span>') +
        (soldOut ? '<span class="ps-card__flag">Sold out</span>' : onSale ? '<span class="ps-card__flag ps-card__flag--sale">Sale</span>' : '') +
        '</span>' +
        '<span class="ps-card__body">' +
        (product.vendor ? '<span class="ps-card__vendor">' + escapeHtml(product.vendor) + '</span>' : '') +
        '<span class="ps-card__title">' +
        highlight(product.title, term) +
        '</span>' +
        '<span class="ps-card__price">' +
        (price ? '<span>' + price + '</span>' : '') +
        (onSale ? '<s>' + compare + '</s>' : '') +
        '</span>' +
        '</span></a>' +
        (this.settings.quickAdd && single && single.available !== false && !soldOut
          ? '<button type="button" class="ps-card__add" data-ps-add="' + escapeHtml(String(single.id)) + '">Add to cart</button>'
          : '') +
        '</article>'
      );
    }

    /* ------------------------------------------------------ interactions */

    handleResultsClick(event) {
      var term = event.target.closest('[data-ps-term]');
      if (term) {
        this.input.value = term.dataset.psTerm;
        this.input.focus();
        this.search(term.dataset.psTerm);
        return;
      }

      var clear = event.target.closest('[data-ps-clear]');
      if (clear) {
        storageSet(clear.dataset.psClear === 'queries' ? STORE_KEYS.queries : STORE_KEYS.products, []);
        this.renderIdle();
        return;
      }

      var forget = event.target.closest('[data-ps-forget]');
      if (forget) {
        var value = forget.dataset.psForget;
        storageSet(
          STORE_KEYS.queries,
          storageGet(STORE_KEYS.queries).filter(function (q) {
            return q !== value;
          })
        );
        this.renderIdle();
        return;
      }

      var add = event.target.closest('[data-ps-add]');
      if (add) {
        event.preventDefault();
        this.addToCart(add);
        return;
      }

      var remember = event.target.closest('[data-ps-remember]');
      if (remember) {
        try {
          this.rememberProducts([JSON.parse(remember.dataset.psRemember)], true);
        } catch (e) {
          /* ignore malformed payload */
        }
        if (this.lastQuery) this.rememberQuery(this.lastQuery);
        return;
      }

      var rememberQuery = event.target.closest('[data-ps-remember-query]');
      if (rememberQuery) {
        this.rememberQuery(rememberQuery.dataset.psRememberQuery);
      }
    }

    handleKeydown(event) {
      if (!this.isOpen) return;
      var options = Array.prototype.slice.call(this.results.querySelectorAll('.ps-option'));

      switch (event.key) {
        case 'ArrowDown':
          if (!options.length) return;
          event.preventDefault();
          this.setActive(options, this.activeIndex + 1);
          break;
        case 'ArrowUp':
          if (!options.length) return;
          event.preventDefault();
          this.setActive(options, this.activeIndex - 1);
          break;
        case 'Home':
          if (!options.length || document.activeElement !== this.input) return;
          event.preventDefault();
          this.setActive(options, 0);
          break;
        case 'End':
          if (!options.length || document.activeElement !== this.input) return;
          event.preventDefault();
          this.setActive(options, options.length - 1);
          break;
        case 'Enter':
          if (this.activeIndex > -1 && options[this.activeIndex]) {
            event.preventDefault();
            options[this.activeIndex].click();
          }
          break;
        default:
          break;
      }
    }

    setActive(options, index) {
      if (index < 0) index = options.length - 1;
      if (index >= options.length) index = 0;
      options.forEach(function (el) {
        el.classList.remove('is-active');
        el.removeAttribute('aria-selected');
      });
      var el = options[index];
      el.classList.add('is-active');
      el.setAttribute('aria-selected', 'true');
      el.id = el.id || 'ps-option-' + index;
      this.input.setAttribute('aria-activedescendant', el.id);
      el.scrollIntoView({ block: 'nearest' });
      this.activeIndex = index;
    }

    clearActive() {
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
    }

    /* ------------------------------------------------------------ memory */

    rememberQuery(term) {
      if (!term) return;
      var list = storageGet(STORE_KEYS.queries).filter(function (q) {
        return q.toLowerCase() !== term.toLowerCase();
      });
      list.unshift(term);
      storageSet(STORE_KEYS.queries, list.slice(0, this.settings.recentLimit));
    }

    rememberProducts(products, pinFirst) {
      if (!products || !products.length) return;
      var keep = products.slice(0, pinFirst ? 1 : 4).map(function (p) {
        return {
          title: p.title,
          url: p.url,
          image: p.featured_image ? p.featured_image.url : p.image,
          price: p.price,
          compare_at_price: p.compare_at_price,
          vendor: p.vendor,
          available: p.available,
          variants: [],
        };
      });

      var existing = storageGet(STORE_KEYS.products);
      var merged = keep.concat(
        existing.filter(function (item) {
          return !keep.some(function (k) {
            return k.url === item.url;
          });
        })
      );
      storageSet(STORE_KEYS.products, merged.slice(0, this.settings.productMemory));
    }

    /* ------------------------------------------------------------- cart */

    addToCart(button) {
      var self = this;
      var id = button.dataset.psAdd;
      var original = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: 1 }] }),
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw new Error(data.description || 'Could not add this item');
            return data;
          });
        })
        .then(function () {
          button.textContent = 'Added';
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          self.refreshCartCount();
          setTimeout(function () {
            button.textContent = original;
            button.disabled = false;
          }, 1600);
        })
        .catch(function (error) {
          button.textContent = error.message.length < 30 ? error.message : 'Unavailable';
          setTimeout(function () {
            button.textContent = original;
            button.disabled = false;
          }, 2200);
        });
    }

    refreshCartCount() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          // Custom themes: a badge element marked data-gg-cart-count.
          document.querySelectorAll('[data-gg-cart-count]').forEach(function (badge) {
            badge.textContent = cart.item_count;
            badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
          });
          document.dispatchEvent(
            new CustomEvent('gg:cart:updated', { bubbles: true, detail: { cart: cart } })
          );
        })
        .catch(function () {
          /* the item was added; only the badge is stale */
        });

      // Dawn fallback, harmless when the section does not exist.
      fetch('/?section_id=cart-icon-bubble')
        .then(function (r) {
          return r.text();
        })
        .then(function (text) {
          var fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section');
          var target = document.getElementById('cart-icon-bubble');
          if (fresh && target) target.innerHTML = fresh.innerHTML;
        })
        .catch(function () {});
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }
  }

  if (!customElements.get('premium-search')) {
    customElements.define('premium-search', PremiumSearch);
  }
})();
